/**
 * `AgentChannelConnectCta` — the CONNECT affordance for a channel's own tab,
 * redesigned as the §3.7 PERSUASIVE EMPTY STATE (assistant-hub S2): this is
 * the evaluation-phase front door, so it opens with the benefit (headline +
 * proof bullets from the §3.7 copy deck) and offers the one-touch primary
 * [Conectar]. On WhatsApp — while NO IM is connected — it carries the
 * Telegram-first nudge (§2.9: steering by prominence, never gating).
 *
 * One tap opens the SAME proven `ChannelConnectDialog` the card's one-touch
 * button drives (QR + owner-pairing for WhatsApp/Telegram), so there is a
 * single connect surface, reached from wherever the lawyer already is.
 *
 * Deliberately NOT the `AgentChannelPlaceholder`: that component is the honest
 * read-only zero/empty state and carries the "these views are for monitoring
 * only" note — which directly contradicts a Connect button.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { useChannelEndpoints } from "@/hooks/useChannelEndpoints";
import {
  ChannelConnectDialog,
  type ConnectChannel,
} from "@/components/contacts/ChannelConnectDialog";

export function AgentChannelConnectCta({
  channel,
  icon: Icon,
  onClose,
  onOpenChange,
  onGoToTelegram,
}: {
  channel: ConnectChannel;
  icon: LucideIcon;
  /** Fired after the dialog closes — lets the host re-read channel status so a
   *  pairing completed in the dialog reflects without a manual reload. */
  onClose?: () => void;
  /**
   * Fired with the dialog's open state on every transition.
   *
   * The dialog stays owned HERE; this only lets a host mirror the state. The
   * host that needs it is `ChannelPane`, whose status-first gate renders this
   * component in an early-return branch: the moment pairing flips the inventory
   * `status` to connected, the branch swaps and an open wizard is destroyed
   * mid-flow — the lawyer's dialog evicted from the tree rather than broken
   * inside (`ai-docs/lexide-agent-tab-connect-continuity-miniplan.md`, I-4).
   * Holding this state lets that branch survive until the lawyer closes it.
   *
   * Purely additive: absent the prop, behaviour is byte-identical.
   */
  onOpenChange?: (open: boolean) => void;
  /** The WhatsApp tab's Telegram-first nudge target (switches the tab). */
  onGoToTelegram?: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpenState] = useState(false);
  // ONE setter for both transitions, so a host can never observe a state this
  // component does not have. Wiring the callback at each call site instead
  // would make a future third site silently un-mirrored — and the host uses
  // this to decide whether to keep the dialog MOUNTED, so a missed edge is a
  // vanishing wizard, not a cosmetic drift.
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };

  // The nudge shows only while NO IM is connected (§3.7 deck) — the moment
  // Telegram's own door is live the steering line disappears. Probed only on
  // the WhatsApp tab; elsewhere the hook idles.
  const tg = useChannelEndpoints("telegram", { enabled: channel === "whatsapp" });
  const showNudge = channel === "whatsapp" && tg.ep1 !== true && !!onGoToTelegram;

  return (
    <div
      data-testid={`channel-connect-cta-${channel}`}
      className="flex flex-col items-center justify-center rounded-lg border border-dashed border-cream-300 px-6 py-14 text-center dark:border-coffee-700"
    >
      <Icon size={28} className="text-cream-500 dark:text-coffee-600" aria-hidden />
      {/* §3.7: benefit headline + proofs — the deck the connect dialog also
          opens with, so the tab and the dialog tell one continuous story. */}
      <h3 className="mt-4 max-w-lg font-serif-display text-xl leading-snug text-coffee-900 dark:text-cream-100">
        {t(`channels.benefit.${channel}.headline`)}
      </h3>
      <ul className="mt-3 space-y-1.5 text-left">
        {([1, 2, 3] as const).map((n) => {
          const text = t(`channels.benefit.${channel}.proof${n}`, { defaultValue: "" });
          if (!text) return null;
          return (
            <li
              key={n}
              className="flex max-w-md items-start gap-2 text-body-sm text-coffee-700 dark:text-cream-300"
            >
              <span aria-hidden className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold-500" />
              <span>{text}</span>
            </li>
          );
        })}
      </ul>
      <button
        type="button"
        data-testid={`channel-connect-start-${channel}`}
        onClick={() => setOpen(true)}
        className={cn(
          "mt-5 rounded-md px-4 py-2 text-body-sm font-semibold transition-colors",
          // Telegram = the starting channel → PRIMARY gold. WhatsApp stays
          // fully available but secondary while the product steers (§2.9).
          channel === "telegram"
            ? "bg-gold-500 text-coffee-900 hover:bg-gold-400"
            : "border border-coffee-800 bg-coffee-800 text-cream-50 hover:bg-coffee-900 dark:border-cream-100 dark:bg-cream-100 dark:text-coffee-900 dark:hover:bg-cream-200",
        )}
      >
        {t("agent.channels.connect.cta")}
      </button>
      {showNudge && (
        <p
          data-testid="channel-connect-telegram-nudge"
          className="mt-3 max-w-md text-xs text-coffee-500 dark:text-cream-500"
        >
          {t("channels.benefit.whatsappNudgePrefix")}{" "}
          <button
            type="button"
            onClick={onGoToTelegram}
            className="font-semibold text-gold-700 underline-offset-2 hover:underline dark:text-gold-400"
          >
            {t("channels.benefit.whatsappNudgeLink")}
          </button>{" "}
          {t("channels.benefit.whatsappNudgeSuffix")}
        </p>
      )}
      {open && (
        <ChannelConnectDialog
          channel={channel}
          onClose={() => {
            setOpen(false);
            onClose?.();
          }}
        />
      )}
    </div>
  );
}
